// ============================================
// settingsRegistry.js - the ONLY surface the chat assistant (Grok) may use to
// change UI settings. Each setting is DEFINED (metadata) and BOUND (get/set
// supplied by whatever component owns the state). Anything that could place
// or cancel orders, touch a wallet, or drive the bot is rejected outright by
// a hard blocklist at define time AND again at every set.
//
// def: { id, label, group, type: 'bool'|'number'|'enum'|'color',
//        min, max, step, integer, options: [{value,label}], scope: 'chart'|'app'|'trade',
//        menu: string (opened before the change), highlight: data-setting-id to flash
//        (defaults to id), confirm: bool (user must approve), format: v => string }
// ============================================

const defs = new Map()       // id -> def
const binds = new Map()      // id -> { get, set }
const menus = new Map()      // name -> open fn

// Hard blocklist: any id SEGMENT (split on . _ - and camelCase) matching these.
const BLOCK = new Set([
  'bot', 'bots', 'wallet', 'wallets', 'order', 'orders', 'swap', 'swaps',
  'execute', 'exec', 'place', 'cancel', 'withdraw', 'transfer', 'send',
  'sign', 'key', 'keys', 'seed', 'secret', 'private', 'arm', 'disarm',
  'position', 'positions', 'close', 'liquidate', 'approve',
])
const segments = (id) => String(id)
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .toLowerCase()
  .split(/[^a-z0-9]+/)
  .filter(Boolean)

export function isBlocked(id) {
  return segments(id).some(s => BLOCK.has(s))
}

const TYPES = new Set(['bool', 'number', 'enum', 'color'])

export function defineSetting(def) {
  if (!def || typeof def.id !== 'string' || !def.id) throw new Error('setting: id required')
  if (isBlocked(def.id) || isBlocked(def.group || '')) throw new Error(`setting: blocked id ${def.id}`)
  if (!TYPES.has(def.type)) throw new Error(`setting ${def.id}: bad type ${def.type}`)
  if (def.type === 'enum' && !(Array.isArray(def.options) && def.options.length)) throw new Error(`setting ${def.id}: enum needs options`)
  if (def.type === 'number' && !(Number.isFinite(def.min) && Number.isFinite(def.max) && def.max >= def.min)) throw new Error(`setting ${def.id}: number needs min/max`)
  const d = { scope: 'app', group: 'General', highlight: def.id, ...def }
  defs.set(d.id, Object.freeze(d))
  return d
}

export function bindSetting(id, binding) {
  if (!defs.has(id)) throw new Error(`bind: unknown setting ${id}`)
  if (!binding || typeof binding.get !== 'function' || typeof binding.set !== 'function') throw new Error(`bind ${id}: get/set required`)
  binds.set(id, binding)
  return () => { if (binds.get(id) === binding) binds.delete(id) }
}

export function registerMenu(name, open) {
  if (typeof open !== 'function') throw new Error('menu: open fn required')
  menus.set(name, open)
  return () => { if (menus.get(name) === open) menus.delete(name) }
}

export function openMenu(name) {
  const fn = menus.get(name)
  if (!fn) return false
  try { fn(); return true } catch { return false }
}
export function hasMenu(name) { return menus.has(name) }

export function getDef(id) { return defs.get(id) || null }

export function getValue(id) {
  const b = binds.get(id)
  if (!b) return undefined
  try { return b.get() } catch { return undefined }
}

/** Validate (never clamp - out of range is an error the caller reports). */
export function validateValue(def, raw) {
  if (!def) return { ok: false, error: 'unknown setting' }
  switch (def.type) {
    case 'bool': {
      if (typeof raw === 'boolean') return { ok: true, value: raw }
      if (raw === 'true' || raw === 'on' || raw === 1 || raw === '1') return { ok: true, value: true }
      if (raw === 'false' || raw === 'off' || raw === 0 || raw === '0') return { ok: true, value: false }
      return { ok: false, error: 'expected true/false' }
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : parseFloat(raw)
      if (!Number.isFinite(n)) return { ok: false, error: 'expected a number' }
      if (def.integer && !Number.isInteger(n)) return { ok: false, error: 'expected a whole number' }
      if (n < def.min || n > def.max) return { ok: false, error: `out of range ${def.min}..${def.max}` }
      return { ok: true, value: n }
    }
    case 'enum': {
      const hit = def.options.find(o => o.value === raw || String(o.value) === String(raw)
        || String(o.label).toLowerCase() === String(raw).toLowerCase())
      return hit ? { ok: true, value: hit.value } : { ok: false, error: `expected one of ${def.options.map(o => o.value).join(', ')}` }
    }
    case 'color': {
      const s = String(raw || '').trim()
      if (/^#[0-9a-f]{6}$/i.test(s)) return { ok: true, value: s.toLowerCase() }
      if (/^#[0-9a-f]{3}$/i.test(s)) return { ok: true, value: ('#' + s.slice(1).split('').map(c => c + c).join('')).toLowerCase() }
      return { ok: false, error: 'expected #rrggbb' }
    }
    default: return { ok: false, error: 'bad type' }
  }
}

export function formatValue(def, v) {
  if (!def) return String(v)
  try { if (typeof def.format === 'function') return def.format(v) } catch {}
  return v === undefined ? '-' : String(v)
}

/** Settings with a live binding (optionally one group), plus current values. */
export function listSettings(group) {
  const out = []
  for (const d of defs.values()) {
    if (!binds.has(d.id)) continue
    if (group && d.group !== group) continue
    const { format, ...meta } = d
    out.push({ ...meta, value: getValue(d.id) })
  }
  return out
}

/** Apply a validated value. Re-checks the blocklist and the binding. */
export function applyValue(id, value) {
  if (isBlocked(id)) return { ok: false, error: 'blocked' }
  const b = binds.get(id)
  if (!b) return { ok: false, error: 'not available here' }
  try { b.set(value); return { ok: true } } catch (e) { return { ok: false, error: String(e?.message || e) } }
}